export const LEAGUE_TAB_NAMES = [
  'picks',
  'draft',
  'scores',
  'leaderboard',
  'bracket',
  'standings',
  'rosters',
  'chat',
  'stats',
  'settings',
] as const;

export type LeagueTabName = (typeof LEAGUE_TAB_NAMES)[number];

export type LeagueTabVisibilityInput = {
  isMarchMadness: boolean;
  showsTournamentBracket: boolean;
  isChatEnabled: boolean;
  showSettingsTab: boolean;
};

/**
 * Android's Material tab bar throws if it is given more than five items.
 * iOS system tabs do not have that hard cap.
 */
export const ANDROID_NATIVE_TAB_LIMIT = 5;

export function isLeagueTabName(value: string | undefined): value is LeagueTabName {
  return typeof value === 'string' && (LEAGUE_TAB_NAMES as readonly string[]).includes(value);
}

export function getVisibleLeagueTabs(input: LeagueTabVisibilityInput): LeagueTabName[] {
  return LEAGUE_TAB_NAMES.filter((name) => isLeagueTabVisible(name, input));
}

export function isLeagueTabVisible(name: LeagueTabName, input: LeagueTabVisibilityInput): boolean {
  switch (name) {
    case 'picks':
    case 'scores':
    case 'leaderboard':
      return !input.isMarchMadness;
    case 'stats':
      return false;
    case 'draft':
    case 'standings':
    case 'rosters':
      return input.isMarchMadness;
    case 'bracket':
      return input.showsTournamentBracket;
    case 'chat':
      return input.isChatEnabled;
    case 'settings':
      return input.showSettingsTab;
    default:
      return false;
  }
}

export function hiddenLeagueTabFallback(
  section: LeagueTabName,
  input: LeagueTabVisibilityInput,
): LeagueTabName {
  if (section === 'chat' && input.isMarchMadness) {
    return 'standings';
  }
  if (input.isMarchMadness) {
    return 'draft';
  }
  return 'picks';
}

export function usesNativeLeagueTabs(platform: string, visibleCount: number): boolean {
  if (platform === 'ios') {
    return true;
  }
  if (platform === 'android') {
    return visibleCount <= ANDROID_NATIVE_TAB_LIMIT;
  }
  return false;
}
